import React, { useState, useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import type { DataRecord, SocialQueueItem } from "../../../types";
import { aspectoLabel, formatDateShort, stripGoogleTranslation } from "../socialFormat";
import { getClientBrandMeta } from "../socialBrand";

interface Props {
  clientSlug: string;
  activePlatform: string;
  comments: DataRecord[];
  windowDays: number;
  onOpenReplyModal: (item: SocialQueueItem) => void;
  onSelectCommentForModal: (item: DataRecord) => void;
  getDirectLink: (row: any) => string;
  renderStars: (rating: number, color?: string) => React.ReactNode;
  renderPlatformBadge: () => React.ReactNode;
}

export function SocialVoiceSection({
  clientSlug,
  activePlatform,
  comments,
  windowDays,
  onOpenReplyModal,
  onSelectCommentForModal,
  getDirectLink,
  renderStars,
  renderPlatformBadge,
}: Props) {
  const brandMeta = getClientBrandMeta(clientSlug);
  const [sentimentFilter, setSentimentFilter] = useState<"ALL" | "negativa" | "neutra" | "positiva">("ALL");
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [visibleReviewCount, setVisibleReviewCount] = useState(8);

  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);
  const number = (v: any, fallback = 0) => {
    const n = Number(v);
    return isNaN(n) ? fallback : n;
  };

  const isMetaIg = activePlatform === "meta_ig" || activePlatform === "instagram";
  const isMetaFb = activePlatform === "meta_fb" || activePlatform === "facebook";
  const isGoogle = activePlatform === "gbp" || activePlatform === "google";
  const isGlobal = activePlatform === "all" || !activePlatform;

  // Filtrado de comentarios para la sección 02
  const filteredReviews = useMemo(() => {
    return comments.filter((r) => {
      if (sentimentFilter !== "ALL") {
        const pol = text(r.polaridad, "").toLowerCase();
        if (pol !== sentimentFilter) return false;
      }
      if (unansweredOnly && Boolean(r.has_reply)) {
        return false;
      }
      return true;
    });
  }, [comments, sentimentFilter, unansweredOnly]);

  const displayedReviews = filteredReviews.slice(0, visibleReviewCount);

  return (
    <section id="section-social-voice" className="social-editorial-section">
      <div className="social-section-tag">{brandMeta.voiceSectionTag}</div>
      <h2 className="social-section-title">
        {isMetaIg
          ? "¿Qué dicen los comentarios en Instagram?"
          : isMetaFb
          ? "¿Qué dicen los comentarios en Facebook?"
          : isGoogle
          ? "¿Qué dicen las reseñas en Google Business Profile?"
          : "¿Qué dice la comunidad en redes y reseñas?"}
      </h2>
      <p className="social-section-subtitle">
        {isMetaIg
          ? `Comentarios en publicaciones y anuncios de Instagram de los últimos ${windowDays} días. Cada uno trae opción de respuesta directa.`
          : isMetaFb
          ? `Comentarios en publicaciones y anuncios de Facebook de los últimos ${windowDays} días. Cada uno trae opción de respuesta directa.`
          : isGoogle
          ? `Las reseñas de los últimos ${windowDays} días en Google BP, con enlace directo y respuesta sugerida.`
          : `Comentarios y reseñas de los últimos ${windowDays} días en todos los canales.`}
      </p>

      <div className="social-editorial-badges">
        {renderPlatformBadge()}
        <span className="social-role-badge">Comunicaciones</span>
        <span className="social-role-badge">Marketing</span>
      </div>

      {/* Filtros de la sección 02 */}
      <div className="social-editorial-filters">
        <button
          type="button"
          className={`social-filter-tab-btn ${sentimentFilter === "ALL" ? "is-active" : ""}`}
          onClick={() => setSentimentFilter("ALL")}
        >
          Todo sentimiento
        </button>
        <button
          type="button"
          className={`social-filter-tab-btn ${sentimentFilter === "negativa" ? "is-active" : ""}`}
          onClick={() => setSentimentFilter("negativa")}
        >
          Negativos
        </button>
        <button
          type="button"
          className={`social-filter-tab-btn ${sentimentFilter === "neutra" ? "is-active" : ""}`}
          onClick={() => setSentimentFilter("neutra")}
        >
          Neutros
        </button>
        <button
          type="button"
          className={`social-filter-tab-btn ${sentimentFilter === "positiva" ? "is-active" : ""}`}
          onClick={() => setSentimentFilter("positiva")}
        >
          Positivos
        </button>

        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            color: "var(--muted, #8e96a8)",
            cursor: "pointer",
            marginLeft: "12px",
          }}
        >
          <input
            type="checkbox"
            checked={unansweredOnly}
            onChange={(e) => setUnansweredOnly(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          <span>Solo sin responder</span>
        </label>
      </div>

      <div style={{ fontSize: "12px", color: "var(--dim, #6d7587)", marginBottom: "14px" }}>
        Mostrando {Math.min(visibleReviewCount, filteredReviews.length)} de {filteredReviews.length} {isGoogle ? "reseñas" : isGlobal ? "interacciones" : "comentarios"} de los últimos {windowDays} días
      </div>

      {displayedReviews.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center", background: "var(--surface, #11141b)", borderRadius: "12px", color: "var(--muted, #8e96a8)", fontSize: "13px" }}>
          No hay comentarios que coincidan con los filtros seleccionados.
        </div>
      ) : (
        <div className="social-cards-grid-4">
          {displayedReviews.map((row) => {
            const rowPlat = text(row.platform, isGoogle ? "gbp" : isMetaIg ? "meta_ig" : isMetaFb ? "meta_fb" : "gbp").toLowerCase();
            const isGbpRow = rowPlat === "gbp" || rowPlat.includes("google");
            const pol = text(row.polaridad, "neutra").toLowerCase();
            const isPaid = text(row.origin).toLowerCase() === "paid";
            const borderClass = pol === "negativa" ? "border-negative" : pol === "positiva" ? "border-positive" : "border-neutral";
            const rawMessage = text(row.message);
            const cleanedMessage = stripGoogleTranslation(rawMessage);
            const isLong = cleanedMessage.length > 175;

            const queueItem: SocialQueueItem = {
              feedback_id: text(row.feedback_id),
              date: text(row.date),
              created_time: text(row.date),
              platform: rowPlat,
              origin: text(row.origin, "organic"),
              author_name: text(row.author_name, "Anónimo"),
              message: rawMessage,
              star_rating: row.star_rating !== null && row.star_rating !== undefined ? number(row.star_rating) : null,
              has_reply: Boolean(row.has_reply),
              post_id: text(row.post_id),
              ad_account_id: (row as any).ad_account_id || null,
              page_id: text(row.page_id),
              action_link: (row as any).action_link || null,
              polaridad: pol,
              acto_de_habla: text(row.acto_de_habla, "consulta"),
              aspecto: text(row.aspecto, "servicio"),
              plan: {
                action: pol === "negativa" ? "escalate" : "reply",
                priority: pol === "negativa" ? 1 : 2,
                reason: `Comentario clasificado como ${pol}`,
                template_key: "standard",
                draft_text: `Hola ${text(row.author_name, "estimado/a")}. Gracias por comunicarte con ${brandMeta.displayName}. Queremos ayudarte y resolver tus inquietudes a la brevedad. ¿Podrías escribirnos por mensaje privado para asistirte directamente?`,
              },
            };

            const postTitle = text(row.post_title);
            const hasPostTitle = Boolean(
              postTitle &&
              postTitle !== "—" &&
              !postTitle.startsWith("SIN_") &&
              postTitle.length > 3
            );

            return (
              <div key={text(row.feedback_id) || Math.random().toString()} className={`social-editorial-card ${borderClass}`}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 750,
                          padding: "2px 7px",
                          borderRadius: "4px",
                          background: isGbpRow
                            ? "rgba(52, 168, 83, 0.15)"
                            : rowPlat.includes("ig")
                            ? "rgba(225, 48, 108, 0.15)"
                            : "rgba(24, 119, 242, 0.15)",
                          color: isGbpRow ? "#34a853" : rowPlat.includes("ig") ? "#e1306c" : "#1877f2",
                        }}
                      >
                        {isGbpRow ? "Google BP" : rowPlat.includes("ig") ? "Instagram" : "Facebook"}
                      </span>

                      <span
                        style={{
                          fontSize: "10.5px",
                          fontWeight: 650,
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background: isPaid ? "rgba(255, 159, 67, 0.15)" : "var(--surface-2, rgba(255, 255, 255, 0.06))",
                          color: isPaid ? "var(--orange, #ff9f43)" : "var(--muted, #8e96a8)",
                          border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
                        }}
                      >
                        {isPaid ? "Anuncio (Paid)" : "Orgánico"}
                      </span>

                      {isGbpRow && row.star_rating !== null && row.star_rating !== undefined && (
                        renderStars(number(row.star_rating), pol === "negativa" ? "#ff5c5c" : "#f59e0b")
                      )}
                    </div>

                    <span className={`social-sentiment-badge pol-${pol}`}>
                      {pol === "positiva" ? "Positivo" : pol === "negativa" ? "Negativo" : "Neutro"}
                    </span>
                  </div>

                  <div style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", marginBottom: "4px" }}>
                    <span>{text(row.author_name) || "Anónimo"}</span>
                    <span> · </span>
                    <span>{formatDateShort(text(row.date))}</span>
                  </div>

                  {hasPostTitle && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--muted, #8e96a8)",
                        marginBottom: "6px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontStyle: "italic",
                      }}
                      title={postTitle}
                    >
                      📍 Post: {postTitle}
                    </div>
                  )}

                  <p style={{ margin: "6px 0 8px 0", fontSize: "12.5px", color: "var(--text, #f6f7fb)", lineHeight: 1.45 }}>
                    "{isLong ? `${cleanedMessage.slice(0, 175)}…` : cleanedMessage}"
                  </p>

                  {isLong && (
                    <button
                      type="button"
                      onClick={() => onSelectCommentForModal(row)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--blue, #7d96ff)",
                        fontSize: "11.5px",
                        cursor: "pointer",
                        padding: "0 0 6px 0",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      Ver comentario completo
                    </button>
                  )}

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", marginTop: "6px" }}>
                    {!Boolean(row.has_reply) && (
                      <span className="social-badge-unanswered">
                        Sin responder
                      </span>
                    )}
                    {Boolean(row.has_reply) && (
                      <span style={{ fontSize: "10.5px", color: "var(--green, #2ed573)", fontWeight: 650 }}>
                        ✓ Respondido
                      </span>
                    )}
                    {Boolean(row.aspecto) && (
                      <span className="social-role-badge" style={{ fontSize: "10.5px" }}>
                        {aspectoLabel(text(row.aspecto))}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "10px", borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.06))", marginTop: "8px" }}>
                  <a
                    href={getDirectLink(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", display: "inline-flex", alignItems: "center", gap: "3px", textDecoration: "none" }}
                  >
                    {isGbpRow ? "Ver en Maps" : rowPlat.includes("ig") ? "Ver en Instagram" : rowPlat.includes("fb") ? "Ver en Facebook" : "Ver publicación"} <ArrowUpRight size={11} />
                  </a>
                  <button
                    type="button"
                    onClick={() => onOpenReplyModal(queueItem)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: isGbpRow ? "#34a853" : "var(--blue, #7d96ff)",
                      fontSize: "11.5px",
                      fontWeight: 650,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: 0,
                    }}
                  >
                    Responder
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visibleReviewCount < filteredReviews.length && (
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          <button
            type="button"
            className="social-filter-tab-btn"
            onClick={() => setVisibleReviewCount((c) => c + 8)}
            style={{ padding: "8px 24px", fontSize: "12.5px" }}
          >
            Ver más
          </button>
        </div>
      )}
    </section>
  );
}
